import CommentIcon from "@mui/icons-material/Comment";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { keyframes, styled } from "@mui/material/styles";
import { RootState } from "_shared/_stores/redux-store";
import { useSelector } from "react-redux";
import { getPendingCommentsCountByField } from "work/proposals/comments/_store/comments-redux-slice";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.1);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.85;
  }
`;
const HighlightIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "muted",
})<{
  active?: boolean;
  muted?: boolean;
}>(({ active, muted, theme }) => ({
  position: "relative",
  zIndex: 0,
  ...(muted && {
    color: theme.palette.action.disabled,
  }),
  ...(active && {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(21, 101, 192, 0.05)",
      borderRadius: "50%",
      animation: `${pulse} 1.5s ease-in-out infinite`,
      zIndex: -1,
    },
  }),
}));

const BadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const CommentBadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 0,
  "& > .MuiBadge-root + .MuiBadge-root": {
    marginLeft: "-6px",
  },
  "& > .MuiBadge-root:first-of-type": {
    zIndex: 1,
  },
  "& > .MuiBadge-root:last-of-type": {
    zIndex: 2,
  },
}));

const PendingBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontSize: "0.58rem",
    height: "16px",
    minWidth: "16px",
    opacity: 1,
    zIndex: 1,
  },
}));

const CommentCountBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    fontSize: "0.58rem",
    height: "16px",
    minWidth: "16px",
  },
}));

type CommentsButtonProps = {
  commentThreads?: CommentThread[];
  field: ProposalField;
  toolTip: string;
  disabled?: boolean;
  onCommentsClicked?: () => void;
  hideUnreadCount?: boolean;
  isActive?: boolean;
  muted?: boolean;
};

export default function CommentsButton(props: Readonly<CommentsButtonProps>) {
  const { commentThreads, field, toolTip, onCommentsClicked, hideUnreadCount, isActive, disabled, muted } = props;
  const proposalId = commentThreads?.[0]?.proposalId;

  const unreadCount = field
    ? commentThreads
      ?.filter((thread) => thread.field.isEqualTo(field))
      .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0
    : 0;
  const pendingCount = useSelector((state: RootState) =>
    getPendingCommentsCountByField(field, proposalId)(state),
  );

  const hasComments = commentThreads?.some((thread) => thread.field.isEqualTo(field)) ?? false;
  const hasPendingComments = pendingCount > 0;

  const getBadgeVariant = () => {
    if (hideUnreadCount) {
      return "dot";
    }
    return unreadCount ? "standard" : "dot";
  };

  const getBadgeContent = () => {
    if (hideUnreadCount) {
      return undefined;
    }
    return unreadCount === 0 ? undefined : unreadCount;
  };
  const badgeVariant = getBadgeVariant();
  const badgeContent = getBadgeContent();
  const renderPendingBadge = () => (
    pendingCount > 0 ? (
      <PendingBadge
        color="warning"
        overlap="circular"
        badgeContent={pendingCount}
      />
    ) : null
  );

  return (
    <Tooltip title={toolTip}>
      <span>
        <HighlightIconButton
          disabled={disabled || !onCommentsClicked || (hideUnreadCount && !hasComments && !hasPendingComments)}
          active={isActive && Boolean(unreadCount || pendingCount)}
          muted={muted}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onCommentsClicked?.();
          }}
        >
          <BadgeContainer>
            <CommentBadgeContainer>

              {badgeVariant === "standard" ? (
                <CommentCountBadge
                  variant="standard"
                  color="secondary"
                  overlap="circular"
                  badgeContent={badgeContent}
                  invisible={!hasComments}
                >
                  {renderPendingBadge()}
                  <CommentIcon fontSize="medium" />
                </CommentCountBadge>
              ) : (
                <Badge
                  variant="dot"
                  color="secondary"
                  overlap="circular"
                  invisible={!hasComments}
                >
                  {renderPendingBadge()}
                  <CommentIcon fontSize="medium" />
                </Badge>
              )}
            </CommentBadgeContainer>
          </BadgeContainer>
        </HighlightIconButton>
      </span>
    </Tooltip>
  );
}
